import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl/dist';
import { useSelector } from 'react-redux';

import { ConditionalRendering } from '../../../../../components/common/conditional-rendering';
import Tooltip from '../../../../../components/common/tooltip/Tooltip';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { getMonthName } from '../../../../../utils/dates/dateMethods';
import { getCurrency } from '../../../../../utils/tools/currencyTools';
import {
    ehbPremiumValue,
    isNotEqualEffectiveDate,
    isPremiumMonthNotInOrignalEffectiveDate,
    isWithinEffectiveRange,
    showPremiumValue,
} from '../../common/enrollmentUtils';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import ColumnApplicationType from './ColumnApplicationType';
import ColumnElectedAPTC from './ColumnElectedAPTC';
import ColumnGrossPremium from './ColumnGrossPremium';
import ColumnMaxAptc from './ColumnMaxAptc';
import ColumnMaxStateSubsidy from './ColumnMaxStateSubsidy';
import ColumnSlcsPremium from './ColumnSlcsPremium';
import ColumnStateSubsidyAmount from './ColumnStateSubsidyAmount';

const PremiumHistoryColumnDef = ({ enrollment }) => {
    const intl = useIntl();
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { enrollmentPremiumFormContext } = useContext(EnrollmentContext);
    const {
        watch,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();
    const { isPremiumEditPermission, isMemberEnrlmntAptcEditable } = usePermissions();

    const getColumnDefination = useMemo(() => {
        const getNetPremium = (premium: IEnrollmentPremiumDto, ehbPercentage: number, planLevel: string) => (
            <>
                <ConditionalRendering isTrue={isWithinEffectiveRange(premium.month, values)}>
                    {getCurrency(premium.netPremiumAmount)}
                </ConditionalRendering>
                <ConditionalRendering
                    isTrue={
                        isNotEqualEffectiveDate(values) &&
                        isWithinEffectiveRange(premium.month, values) &&
                        isPremiumMonthNotInOrignalEffectiveDate(premium.month, values)
                    }
                >
                    <Tooltip
                        id="member.enrollment_list.premiumhistory.pendingCalculation"
                        elementId="netPremium"
                        content={'member.enrollment_list.premiumhistory.pendingCalculation'}
                        place="top"
                    />
                </ConditionalRendering>
            </>
        );

        const columnHeaders = [
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.month' }),
                key: 'month',
                render: (premium: { month: number }) => <span>{getMonthName(premium.month)}</span>,
            },
        ];

        const addColumn = (condition: boolean, column: any) => {
            if (condition) columnHeaders.push(column);
        };

        const columns = [
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.grossPremium' }),
                    key: 'grossPremium',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnGrossPremium premium={premium} />,
                },
            },
            {
                condition: isPremiumEditPermission(userPermissions),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.groupMaxAPTC' }),
                    key: 'maxAptc',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnMaxAptc premium={premium} />,
                },
            },

            {
                condition: isMemberEnrlmntAptcEditable(userPermissions),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.electedAPTC' }),
                    key: 'electedAPTC',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnElectedAPTC premium={premium} />,
                },
            },
            {
                condition:
                    enrollment.isStateSubsidyEnabled &&
                    (userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.groupMaxStateSubsidy' }),
                    key: 'maxStateSubsidy',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnMaxStateSubsidy premium={premium} />,
                },
            },
            {
                condition:
                    enrollment.isStateSubsidyEnabled &&
                    (userPermissions?.memberEnrlmntAptc || userPermissions?.memberEnrlmntAptcEditable),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.electedStateSubsidy' }),
                    key: 'stateSubsidyAmount',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnStateSubsidyAmount premium={premium} />,
                },
            },
            {
                condition: enrollment.statePremiumCreditAmt,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.premiumCredit' }),
                    key: 'premiumCredit',
                    render: (premium: { month: number; statePremiumCreditAmt: number }) => (
                        <>{showPremiumValue(premium, values) ? getCurrency(premium.statePremiumCreditAmt) : ''}</>
                    ),
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.netPremium' }),
                    key: 'netPremium',
                    render: (premium: IEnrollmentPremiumDto) =>
                        getNetPremium(premium, values.ehbPercentage, values.planLevel),
                },
            },
            {
                condition: userPermissions?.memberEnrlmntAppType,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.applicationType' }),
                    key: 'applicationType',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnApplicationType premium={premium} />,
                },
            },
            {
                condition: userPermissions?.memberEnrlmntViewSlcsp || userPermissions?.memberEnrlmntEditSlcsp,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.slcsp' }),
                    key: 'slcspPremiumAmount',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnSlcsPremium premium={premium} />,
                },
            },
            {
                condition: userPermissions?.memberEnrlmntEhbprmum || userPermissions?.memberEnrlmntEhbPrmumEditable,
                column: {
                    titleId: `${intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.ehbPremium' })} (${
                        enrollment.ehbPercentage != null && enrollment.ehbPercentage !== 0
                            ? enrollment.ehbPercentage > 1
                                ? enrollment.ehbPercentage
                                : +(enrollment.ehbPercentage * 100).toFixed(2)
                            : 0
                    } %)`,
                    key: 'ehbPremium',
                    render: (premium: IEnrollmentPremiumDto) => <>{ehbPremiumValue(premium, values)}</>,
                },
            },
        ];

        columns.forEach(({ condition, column }) => addColumn(condition, column));

        return columnHeaders;
    }, [enrollment, userPermissions, intl, values, errors]);

    return getColumnDefination;
};

export default PremiumHistoryColumnDef;
